// ========================================
// WORKHOURS SERVICE COMPLETO AGGIORNATO - Gestisce distribuzione ore
// src/services/workHoursIntegrationService.js
// ========================================

import { 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../firebase';

export const getWorkHoursForDate = async (userId, date, respectDistribution = true, currentRapportinoId = null) => {
  try {
    console.log(`🔍 Recuperando ore per utente ${userId} in data ${date}, respectDistribution: ${respectDistribution}, rapportino: ${currentRapportinoId}`);
    
    // 🆕 NUOVO: Se respectDistribution è true, controlla se ci sono ore già distribuite
    if (respectDistribution) {
      const distributedHours = await checkDistributedHours(userId, date, currentRapportinoId);
      if (distributedHours) {
        console.log(`📊 Ore ${distributedHours.source} trovate per ${userId} data ${date}:`, {
          oreLavorate: distributedHours.oreLavorate,
          oreStandard: distributedHours.oreStandard,
          oreStraordinario: distributedHours.oreStraordinario
        });
        return distributedHours;
      }
    }
    
    const [year, month, day] = date.split('-');
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    
    // CORREZIONE: Prova sia con numeri che con stringhe
    console.log(`📊 Cercando in workHours: month=${monthNum} (number) o "${monthNum}" (string), year=${yearNum} (number) o "${yearNum}" (string)`);
    
    // Prima prova: month e year come numeri
    let workHoursQuery = query(
      collection(db, 'workHours'),
      where('userId', '==', userId),
      where('month', '==', monthNum),
      where('year', '==', yearNum)
    );
    
    let workHoursSnapshot = await getDocs(workHoursQuery);
    
    // Se non trova nulla, prova con stringhe
    if (workHoursSnapshot.empty) {
      console.log(`❌ Non trovato con numeri, provo con stringhe...`);
      
      workHoursQuery = query(
        collection(db, 'workHours'),
        where('userId', '==', userId),
        where('month', '==', monthNum.toString()),
        where('year', '==', yearNum.toString())
      );
      
      workHoursSnapshot = await getDocs(workHoursQuery);
    }
    
    // Se ancora non trova, prova combinazioni miste
    if (workHoursSnapshot.empty) {
      console.log(`❌ Non trovato con stringhe, provo combinazioni miste...`);
      
      // Prova month string, year number
      workHoursQuery = query(
        collection(db, 'workHours'),
        where('userId', '==', userId),
        where('month', '==', monthNum.toString()),
        where('year', '==', yearNum)
      );
      
      workHoursSnapshot = await getDocs(workHoursQuery);
      
      // Se ancora non trova, prova month number, year string
      if (workHoursSnapshot.empty) {
        workHoursQuery = query(
          collection(db, 'workHours'),
          where('userId', '==', userId),
          where('month', '==', monthNum),
          where('year', '==', yearNum.toString())
        );
        
        workHoursSnapshot = await getDocs(workHoursQuery);
      }
    }
    
    if (workHoursSnapshot.empty) {
      console.log(`❌ Nessun calendario ore trovato per utente ${userId} nel mese ${monthNum}/${yearNum}`);
      
      // DEBUG: Mostra tutti i documenti per questo utente
      const allUserDocsQuery = query(
        collection(db, 'workHours'),
        where('userId', '==', userId)
      );
      
      const allUserDocs = await getDocs(allUserDocsQuery);
      console.log(`📚 Documenti esistenti per utente ${userId}:`, 
        allUserDocs.docs.map(doc => ({
          month: doc.data().month,
          year: doc.data().year,
          monthType: typeof doc.data().month,
          yearType: typeof doc.data().year
        }))
      );
      
      return {
        found: false,
        reason: 'Nessun calendario ore trovato per questo mese',
        oreLavorate: 0,
        oreStandard: 0,
        oreStraordinario: 0
      };
    }
    
    const workHoursDoc = workHoursSnapshot.docs[0];
    const entries = workHoursDoc.data().entries || [];
    
    console.log(`📊 Documento trovato per ${monthNum}/${yearNum}, ${entries.length} entries`);

    // Trova l'entry per la data specifica
    const dayEntry = entries.find(entry => entry.date === date);
    
    if (!dayEntry) {
      console.log(`❌ Nessuna entry trovata per la data ${date}`);
      console.log(`📅 Date disponibili:`, entries.map(e => e.date));
      return {
        found: false,
        reason: 'Nessuna timbratura per questa data',
        oreLavorate: 0,
        oreStandard: 0,
        oreStraordinario: 0
      };
    }
    
    console.log(`✅ Entry trovata per ${date}:`, dayEntry);
    
    // Gestisci casi speciali (M, P, A, etc.)
    if (typeof dayEntry.total === 'string' && ['M', 'P', 'A', 'C', 'F'].includes(dayEntry.total)) {
      return {
        found: true,
        isSpecialDay: true,
        specialCode: dayEntry.total,
        specialDescription: getSpecialDayDescription(dayEntry.total),
        oreLavorate: 0,
        oreStandard: 0,
        oreStraordinario: 0,
        note: dayEntry.notes || ''
      };
    }
    
    // Calcola ore normali - COSTO ORARIO FISSO
    const oreStandard = parseFloat(dayEntry.total) || 0;
    const oreStraordinario = parseFloat(dayEntry.overtime) || 0;
    const oreTotali = oreStandard + oreStraordinario;
    
    console.log(`✅ Ore originali da workHours: ${oreStandard}h standard + ${oreStraordinario}h straordinario = ${oreTotali}h totali`);
    
    return {
      found: true,
      isSpecialDay: false,
      oreLavorate: oreTotali,
      oreStandard: oreStandard,
      oreStraordinario: oreStraordinario,
      note: dayEntry.notes || '',
      source: 'workHours',
      dataSync: new Date().toISOString()
    };
    
  } catch (error) {
    console.error('❌ Errore nel recupero ore da workHours:', error);
    return {
      found: false,
      reason: 'Errore nel sistema',
      error: error.message,
      oreLavorate: 0,
      oreStandard: 0,
      oreStraordinario: 0
    };
  }
};

// 🆕 FUNZIONE AGGIORNATA: Gestisce ore specifiche per cantiere vs ore totali
const checkDistributedHours = async (userId, date, currentRapportinoId = null) => {
  try {
    console.log(`🔍 Controllo ore distribuite per ${userId} data ${date}, rapportino corrente: ${currentRapportinoId}`);
    
    // Cerca rapportini con ore distribuite per questo utente e data
    const rapportiniQuery = query(
      collection(db, "rapportini"),
      where("data", "==", new Date(date)),
      where("stato", "==", "Attivo")
    );
    
    const snapshot = await getDocs(rapportiniQuery);
    let targetRapportino = null;
    let allDistributedRapportini = [];
    
    snapshot.forEach(doc => {
      const rapportino = doc.data();
      const personaConOreDistribuite = rapportino.personalePresente?.find(
        p => p.personaId === userId && p.distributedHours === true
      );
      
      if (personaConOreDistribuite) {
        const rapportinoInfo = {
          rapportinoId: doc.id,
          persona: personaConOreDistribuite,
          rapportino: rapportino
        };
        
        allDistributedRapportini.push(rapportinoInfo);
        
        // Se stiamo cercando un rapportino specifico
        if (currentRapportinoId && doc.id === currentRapportinoId) {
          targetRapportino = rapportinoInfo;
        }
      }
    });
    
    // Se non ci sono ore distribuite
    if (allDistributedRapportini.length === 0) {
      console.log(`📊 Nessuna ora distribuita trovata per ${userId} data ${date}`);
      return null;
    }
    
    // CASO 1: Stiamo caricando un rapportino specifico → restituisci le sue ore specifiche
    if (targetRapportino) {
      console.log(`📊 Trovato rapportino specifico ${currentRapportinoId} con ore distribuite`);
      const persona = targetRapportino.persona;
      
      return {
        found: true,
        isDistributed: true,
        isSpecialDay: false,
        // 🆕 IMPORTANTE: Restituisce le ore SPECIFICHE di questo cantiere
        oreStandard: parseFloat(persona.oreStandard) || 0,
        oreStraordinario: parseFloat(persona.oreStraordinario) || 0,
        oreLavorate: parseFloat(persona.oreLavorate) || 0,
        numCantieri: persona.distributionDetails?.numCantieri || 1,
        note: persona.notePersonale || `Ore distribuite: ${persona.oreLavorate}h`,
        source: 'distributed-specific',
        dataSync: new Date().toISOString(),
        distributionInfo: {
          numCantieri: persona.distributionDetails?.numCantieri,
          originalTotal: persona.distributionDetails?.originalTotal,
          assignedHours: persona.distributionDetails?.assignedHours || persona.oreLavorate,
          cantiereIndex: persona.distributionDetails?.cantiereIndex,
          distributionDate: persona.distributionDetails?.distributionDate
        }
      };
    }
    
    // CASO 2: Richiesta generica (anteprima) → restituisci le ore originali totali
    const firstDistribution = allDistributedRapportini[0].persona;
    const distributionDetails = firstDistribution.distributionDetails;
    
    if (distributionDetails) {
      console.log(`📊 Restituisco ore originali totali per anteprima:`, distributionDetails);
      
      // Usa i dati originali salvati nella distribuzione
      const originalStandard = distributionDetails.originalStandard || Math.min(8, distributionDetails.originalTotal || 0);
      const originalOvertime = distributionDetails.originalOvertime || Math.max(0, (distributionDetails.originalTotal || 0) - 8);
      
      return {
        found: true,
        isDistributed: true,
        isSpecialDay: false,
        oreStandard: originalStandard,
        oreStraordinario: originalOvertime,
        oreLavorate: distributionDetails.originalTotal || 0,
        numCantieri: distributionDetails.numCantieri || 1,
        note: `Ore totali disponibili: ${distributionDetails.originalTotal}h (distribuite tra ${distributionDetails.numCantieri} cantieri)`,
        source: 'distributed-total',
        dataSync: new Date().toISOString(),
        distributionInfo: {
          numCantieri: distributionDetails.numCantieri,
          originalTotal: distributionDetails.originalTotal,
          isPreview: true
        }
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ Errore nel controllo ore distribuite:', error);
    return null;
  }
};

/**
 * Recupera ore per tutti gli utenti in una data specifica
 */
export const getAllWorkHoursForDate = async (userIds, date) => {
  console.log(`🔍 Recuperando ore per ${userIds.length} utenti in data ${date}`);
  
  const results = {};
  
  for (const userId of userIds) {
    try {
      results[userId] = await getWorkHoursForDate(userId, date);
      // Piccola pausa per evitare sovraccarico
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error(`❌ Errore per utente ${userId}:`, error);
      results[userId] = {
        found: false,
        reason: 'Errore nel recupero dati',
        error: error.message,
        oreLavorate: 0,
        oreStandard: 0,
        oreStraordinario: 0
      };
    }
  }
  
  console.log(`✅ Recupero completato per ${Object.keys(results).length} utenti`);
  return results;
};

/**
 * 🆕 NUOVA: Recupera ore per un utente in un rapportino specifico
 * Utile quando devi caricare ore per un rapportino esistente
 */
export const getWorkHoursForRapportino = async (userId, date, rapportinoId) => {
  console.log(`🔍 Recuperando ore specifiche per utente ${userId} in rapportino ${rapportinoId} data ${date}`);
  
  // Sempre rispetta la distribuzione e passa l'ID del rapportino
  return await getWorkHoursForDate(userId, date, true, rapportinoId);
};

/**
 * 🆕 NUOVA: Recupera ore originali (ignora distribuzione)
 * Utile per anteprime e controlli
 */
export const getOriginalWorkHours = async (userId, date) => {
  console.log(`🔍 Recuperando ore originali (ignora distribuzione) per utente ${userId} data ${date}`);
  
  // Non rispettare la distribuzione per ottenere sempre ore originali
  return await getWorkHoursForDate(userId, date, false, null);
};

/**
 * Descrizioni per i giorni speciali
 */
const getSpecialDayDescription = (code) => {
  const descriptions = {
    'M': 'Malattia',
    'P': 'Permesso',
    'A': 'Assenza',
    'C': 'Cassa Integrazione',
    'F': 'Ferie'
  };
  return descriptions[code] || 'Giorno speciale';
};

/**
 * Calcola il costo totale per un dipendente
 * IMPORTANTE: Usa costo orario fisso per tutte le ore (standard + straordinario)
 */
export const calculatePersonaleCost = (oreStandard, oreStraordinario, costoOrario) => {
  // Le ore possono arrivare come testo: "8" + "0" concatenerebbe in "80"
  oreStandard = parseFloat(oreStandard) || 0;
  oreStraordinario = parseFloat(oreStraordinario) || 0;
  costoOrario = parseFloat(costoOrario) || 0;
  const oreTotali = oreStandard + oreStraordinario;
  
  // COSTO FISSO: stesso costo orario per tutte le ore
  const costoStandard = oreStandard * costoOrario;
  const costoStraordinario = oreStraordinario * costoOrario;
  const costoTotale = costoStandard + costoStraordinario;
  
  console.log(`💰 Calcolo costi: ${oreStandard}h std + ${oreStraordinario}h straord = ${oreTotali}h @ €${costoOrario}/h = €${costoTotale.toFixed(2)}`);
  
  return {
    oreStandard,
    oreStraordinario,
    oreTotali,
    costoOrario,
    costoStandard,
    costoStraordinario,
    costoTotale,
    dettaglioCalcolo: `${oreTotali}h × €${costoOrario}/h = €${costoTotale.toFixed(2)}`
  };
};

/**
 * 🆕 NUOVA: Verifica se un utente ha ore distribuite in una data
 */
export const hasDistributedHours = async (userId, date) => {
  try {
    const distributedHours = await checkDistributedHours(userId, date, null);
    return distributedHours !== null;
  } catch (error) {
    console.error('Errore nel controllo ore distribuite:', error);
    return false;
  }
};

/**
 * 🆕 NUOVA: Ottieni informazioni sulla distribuzione per un utente
 */
export const getDistributionInfo = async (userId, date) => {
  try {
    const distributedHours = await checkDistributedHours(userId, date, null);
    if (distributedHours && distributedHours.distributionInfo) {
      return {
        hasDistribution: true,
        numCantieri: distributedHours.distributionInfo.numCantieri,
        originalTotal: distributedHours.distributionInfo.originalTotal,
        distributionDate: distributedHours.distributionInfo.distributionDate
      };
    }
    return { hasDistribution: false };
  } catch (error) {
    console.error('Errore nel recupero info distribuzione:', error);
    return { hasDistribution: false, error: error.message };
  }
};

/**
 * Valida se la data è nel formato corretto
 */
export const validateDateFormat = (date) => {
  if (!date || typeof date !== 'string') return false;
  
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) return false;
  
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  
  return dateObj.getFullYear() === year &&
         dateObj.getMonth() === month - 1 &&
         dateObj.getDate() === day;
};

/**
 * Ottieni la data di oggi nel formato YYYY-MM-DD
 */
export const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

/**
 * Formatta una data per la visualizzazione
 */
export const formatDateForDisplay = (date) => {
  try {
    const [year, month, day] = date.split('-');
    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return dateObj.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Errore nel formato data:', error);
    return date;
  }
};